import React, { useEffect, useMemo, useState } from "react";
import {
    countItemsContaining,
    deleteStorage,
    getStorage,
    setStorage,
    countAllSavedChecklists
} from "../utils/storage";
import { Link, useNavigate } from "react-router-dom";
import { useDate } from "../useDate";
import { ActionSheet, ActionSheetButton, AlertDialog, Button, Checkbox, Page, Toolbar } from "react-onsenui";
import { Icon } from "@iconify/react";
import { clNavListStyle, clNavRowStyle, clPageStyle, CL_PRIMARY, CL_PAGE_BG } from "./cl-styles";
import { paths } from "../App";
import { storageCleanReset } from "../utils/storage-clean-reset";
import {
    getUserOrgs,
    getSelectedOrgIds,
    setSelectedOrgIds,
    getOrgSelectionLabel,
} from "../utils/orgSelection";
import Html5QrcodePlugin from "../Html5QrcodePlugin";
import {
    Box,
    Chip,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Typography,
} from "@mui/material";

// ── Component ──────────────────────────────────────────────────────────────
export const ChecklistsDashboard = () => {

    const [authUser, setAuthUser] = useState(getStorage('u4'));
    const [unsubmittedCount, setUnsubmittedCount] = useState(0);

    const defaultRole = { name: "Not set", value: 0 };
    const [authUserRole, setAuthUserRole] = useState(getStorage('u4R'));
    useEffect(() => { setStorage('u4R', authUserRole); }, [authUserRole]);

    const userOrgs = useMemo(() => getUserOrgs(authUser), [authUser]);
    const [orgLabel, setOrgLabel] = useState(() => getOrgSelectionLabel(authUser));
    const [orgPickerOpen, setOrgPickerOpen] = useState(false);
    const [orgDraftIds, setOrgDraftIds] = useState([]);

    const openOrgPicker = () => {
        setOrgDraftIds(getSelectedOrgIds(authUser));
        setOrgPickerOpen(true);
    };
    const toggleOrgDraft = (orgId) => {
        const id = Number(orgId);
        setOrgDraftIds(prev => prev.includes(id) ? prev.filter(o => o !== id) : [...prev, id]);
    };
    const applyOrgSelection = () => {
        setSelectedOrgIds(orgDraftIds, authUser);
        setOrgLabel(getOrgSelectionLabel(authUser));
        setOrgPickerOpen(false);
    };

    useEffect(() => {
        const updateUnsubmittedCount = async () => {
            try {
                const count = await countAllSavedChecklists();
                setUnsubmittedCount(count);
            } catch {
                setUnsubmittedCount(countItemsContaining('cache_instance_'));
            }
        };
        updateUnsubmittedCount();
        const interval = setInterval(updateUnsubmittedCount, 30000);
        return () => clearInterval(interval);
    }, []);

    const navigate = useNavigate();
    const [enableQRScanner, setEnableQRScanner] = React.useState(false);
    const [showOptions, setShowOptions] = useState(false);
    const dateRT = useDate();

    const onNewScanResult = (decodedText) => {
        if (isValidQRCode(decodedText)) {
            navigate(paths.checklist.details, { state: { data: { decodedText } } });
        }
    };
    const performLogout = () => {
        deleteStorage('u4');
        deleteStorage('u4R');
        navigate(paths.root.login);
    };

    const fname = authUser?.fname || '';

    return (
        <Page
            style={clPageStyle}
            renderToolbar={() => (
                <Toolbar style={{
                    backgroundColor: '#ffffff',
                    borderBottom: '1px solid rgba(0,0,0,0.07)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                }}>
                    <div className="left" style={{ display: 'flex', alignItems: 'center', paddingLeft: 10 }}>
                        <span className="fa fa-user-shield" style={{ color: CL_PRIMARY, fontSize: 14, marginRight: 6 }} />
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1a1a1a' }}>
                            {fname || 'User'}
                        </Typography>
                    </div>
                    <div className="center">
                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>{dateRT}</Typography>
                    </div>
                    <div className="right" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 8, minWidth: 44 }}>
                        <IconButton size="small" onClick={() => setShowOptions(true)} sx={{ color: '#555' }}>
                            <Icon icon="mdi:cog-outline" style={{ fontSize: 22 }} />
                        </IconButton>
                    </div>
                </Toolbar>
            )}
        >
            <Box sx={{ px: 2, pt: 2, pb: 12 }}>

                {/* ── Org row — only shown when user has multiple orgs ── */}
                {userOrgs.length > 1 && (
                    <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.09)', backgroundColor: '#fff', mb: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                        <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={openOrgPicker}>
                            <Icon icon="mdi:office-building-outline" style={{ fontSize: 18, color: CL_PRIMARY, flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ flex: 1, color: '#555' }}>Organisation</Typography>
                            <Typography variant="body2" sx={{ color: '#333', fontWeight: 500, maxWidth: 140, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                {orgLabel}
                            </Typography>
                            <Icon icon="mdi:chevron-right" style={{ fontSize: 18, color: 'rgba(0,0,0,0.28)', flexShrink: 0 }} />
                        </Box>
                    </Paper>
                )}

                {/* ── QR scanner ────────────────────────────────────── */}
                {enableQRScanner ? (
                    <Html5QrcodePlugin
                        fps={4}
                        qrbox={550}
                        disableFlip={false}
                        qrCodeSuccessCallback={onNewScanResult}
                    />
                ) : (
                    <Paper
                        elevation={0}
                        onClick={() => setEnableQRScanner(true)}
                        sx={{
                            borderRadius: 2,
                            border: '2px dashed rgba(30,136,229,0.3)',
                            backgroundColor: 'rgba(30,136,229,0.03)',
                            mb: 2.5,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            px: 2.5,
                            py: 2.5,
                            gap: 2,
                            '&:active': { backgroundColor: 'rgba(30,136,229,0.07)' },
                        }}
                    >
                        <Icon icon="mdi:qrcode-scan" style={{ fontSize: 32, color: CL_PRIMARY, opacity: 0.7 }} />
                        <Box>
                            <Typography variant="body2" fontWeight={600} sx={{ color: CL_PRIMARY }}>Scan Checklist QR</Typography>
                            <Typography variant="caption" color="text.secondary">Tap to open camera scanner</Typography>
                        </Box>
                        <Icon icon="mdi:chevron-right" style={{ fontSize: 18, color: 'rgba(0,0,0,0.28)', marginLeft: 'auto' }} />
                    </Paper>
                )}

                {/* DEV LOCAL: Test QR Button */}
                {process.env.NODE_ENV === 'development' && (
                    <Button style={{ margin: '0 0 12px', width: '100%', textAlign: 'center' }} onClick={() => onNewScanResult(
                        "https://atom.checkinn.co/r?hash=eyJpZCI6MjI4LCJnZWVrIjoiQHRoaW5rZGoiLCJicGkiOjEsIm9pIjoxMDAxLCJsb2kiOjEsImJwbiI6Ildhc2hSb29tIFRlbXBsYXRlIiwibG9uIjoiTWVuJ3MgV2FzaHJvb20ifQ=="
                    )}>Scan QR (+!DEV!+)</Button>
                )}

                {/* ── Quick access nav ──────────────────────────────── */}
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.08em', fontWeight: 600, display: 'block', mb: 1 }}>
                    Quick Access
                </Typography>
                <nav style={clNavListStyle}>
                    {[
                        { to: paths.checklist.myChecklists, icon: 'mdi:clipboard-list-outline',  label: 'My Checklists',          sub: 'Create a new checklist' },
                        { to: paths.checklist.upcoming,     icon: 'mdi:calendar-clock',           label: 'Upcoming Checklists',     sub: 'Scheduled for your department' },
                        { to: paths.checklist.myInstances,  icon: 'mdi:check-circle-outline',     label: 'Completed Checklists',    sub: 'View submitted history' },
                        { to: paths.checklist.myClDrafts,   icon: 'mdi:tray-full',                label: 'Unsubmitted Checklists',  sub: 'Saved locally on this device', badge: unsubmittedCount },
                    ].map((item, i, arr) => (
                        <Link key={item.to} to={item.to} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <div style={{ ...clNavRowStyle(i === arr.length - 1), padding: '16px' }}>
                                <Box sx={{
                                    width: 42, height: 42, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    backgroundColor: 'rgba(30,136,229,0.1)', mr: 1.5, flexShrink: 0,
                                }}>
                                    <Icon icon={item.icon} style={{ fontSize: 22, color: CL_PRIMARY }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>{item.label}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>{item.sub}</Typography>
                                </Box>
                                {item.badge > 0 && (
                                    <Chip label={item.badge} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, backgroundColor: CL_PRIMARY, color: '#fff', mr: 1 }} />
                                )}
                                <Icon icon="mdi:chevron-right" style={{ fontSize: 18, color: 'rgba(0,0,0,0.25)', flexShrink: 0 }} />
                            </div>
                        </Link>
                    ))}
                </nav>

            </Box>

            {/* ── Org picker dialog ──────────────────────────────────── */}
            <AlertDialog isOpen={orgPickerOpen} onCancel={() => setOrgPickerOpen(false)} cancelable>
                <div className="alert-dialog-title">Show checklists from</div>
                <div className="alert-dialog-content">
                    {userOrgs.length > 5 && (
                        <div className="flex flex-dir-row flex-horiz-space-bw flex-vert-center">
                            <Button modifier="quiet" onClick={() => setOrgDraftIds(userOrgs.map(o => Number(o.id)))}>Select all</Button>
                            <Button modifier="quiet" onClick={() => setOrgDraftIds([])}>Clear</Button>
                        </div>
                    )}
                    <div style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                        {userOrgs.map(org => (
                            <label key={org.id} className="flex flex-vert-center p-b-10">
                                <Checkbox
                                    checked={orgDraftIds.includes(Number(org.id))}
                                    onChange={() => toggleOrgDraft(org.id)}
                                    modifier="" />
                                <span className="p-l-10">{org.name}</span>
                            </label>
                        ))}
                    </div>
                    {!orgDraftIds.length && (
                        <div className="f-s-08r p-t-5">Nothing selected — all organisations will be shown.</div>
                    )}
                    <div className="flex flex-dir-row flex-horiz-space-bw flex-vert-center p-t-20">
                        <Button modifier="quiet" onClick={() => setOrgPickerOpen(false)}>Cancel</Button>
                        <Button onClick={applyOrgSelection}>Apply</Button>
                    </div>
                </div>
            </AlertDialog>

            {/* ── Options action sheet ───────────────────────────────── */}
            <ActionSheet isOpen={showOptions} isCancelable={false}>
                <ActionSheetButton onClick={() => navigate(paths.checklist.mySettings)}>
                    Settings
                </ActionSheetButton>
                <ActionSheetButton onClick={() => {
                    const confirmed = window.confirm('This will clear all unsubmitted checklists, login info and caches from your device! This action cannot be undone. Are you sure?');
                    if (confirmed) { storageCleanReset(); }
                }}>
                    Reset storage
                </ActionSheetButton>
                <ActionSheetButton onClick={performLogout} modifier="destructive">
                    Logout
                </ActionSheetButton>
                <ActionSheetButton onClick={() => setShowOptions(false)}>
                    Cancel
                </ActionSheetButton>
            </ActionSheet>

        </Page>
    );
};

const isValidQRCode = (string) => !!string;
